// Firebase configuration and initialization
// This connects to the same Firebase project used by the Apps Script system

import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { DEPLOYMENT_CONFIG } from '../config/deploymentConfig';

// Firebase configuration using environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase app
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication
export const auth = getAuth(app);

// Configure Google Auth Provider
export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('profile');
googleProvider.addScope('email');

// Set custom parameters to help with browser compatibility
googleProvider.setCustomParameters({
  prompt: 'select_account'  // Always show account selection
});

// Initialize Firestore. Auto-detect long polling helps in browser/proxy
// environments that block Firestore WebChannel streaming requests.
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true
});

// Initialize Functions (default region)
export const functions = getFunctions(app);

// Initialize Firebase Storage for non-Workspace attachment storage
export const storage = getStorage(app);

// Firebase collection names (matching Apps Script configuration)
export const COLLECTIONS = {
  TRUCKS: process.env.REACT_APP_FIREBASE_COLLECTION_TRUCKS || 'trucks',
  CHECKS: process.env.REACT_APP_FIREBASE_COLLECTION_CHECKS || 'testing-completedTruckChecks',
  INVENTORY: process.env.REACT_APP_FIREBASE_COLLECTION_INVENTORY || 'inventoryItems'
};

// Google Groups for role mapping (matching Apps Script system)
export const GOOGLE_GROUPS = {
  ADMINS: DEPLOYMENT_CONFIG.workspace.groups.admins,
  INSPECTORS: DEPLOYMENT_CONFIG.workspace.groups.inspectors,
  SUPERVISORS: DEPLOYMENT_CONFIG.workspace.groups.supervisors
};

export const WORKSPACE_CONFIG = DEPLOYMENT_CONFIG.workspace;
export const PROVIDER_CONFIG = DEPLOYMENT_CONFIG.providers;

// Application configuration
export const APP_CONFIG = {
  FORM_TITLE: process.env.REACT_APP_FORM_TITLE || DEPLOYMENT_CONFIG.labels.appName,
  MAX_SUBMISSIONS_PER_HOUR: 10,
  MAX_INPUT_LENGTH: 1000
};

// Development mode - connect to Firebase emulators if running locally
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('🔥 Connected to Firebase emulators');
  } catch (error) {
    console.log('⚠️ Firebase emulators not available, using production');
  }
} else {
  // Ensure we're using production functions
  console.log('🔥 Using production Firebase Functions in region: us-central1');
}

// Export the initialized app
export default app;

// Utility function to check if Firebase is properly configured
export const isFirebaseConfigured = () => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  );
};

// Log configuration status (development only)
if (process.env.NODE_ENV === 'development') {
  console.log('🔥 Firebase Configuration:', {
    projectId: firebaseConfig.projectId,
    collections: COLLECTIONS,
    groups: GOOGLE_GROUPS,
    configured: isFirebaseConfigured()
  });
}
